/*
 |------------------------------------------------------------------------------
 | Common Utility Function Library
 |------------------------------------------------------------------------------
 |
 */

import { ContentNode, ContentImageMeta } from "./Types";

export enum NodeMedia {
    Blog = "image",
    Image = "page.image",
    Background = "page.background"
}

const nodeMediaPathMap = new Map([
    [NodeMedia.Blog, "/media/blog"],
    [NodeMedia.Image, "/media/page"],
    [NodeMedia.Background, "/media/background"]
]);

const Utils = {
    /**
     * Convert Pascal case, Camel case and studely CAPS to kebab-case.
     *
     * @param {string} value
     *
     * @returns string
     */
    studlyToKebab (value: string): string {
        return value.replace(/\B(?:([A-Z])(?=[a-z]))|(?:(?<=[a-z0-9])([A-Z]))/g, "-$1$2").toLowerCase();
    },

    /**
     * Get the node media source based on the media type. The lookup is done
     * using dot notation and looping through all the fields in a ContentNode
     * until a match is found.
     *
     * @param  {ContentNode} node
     * @param  {NodeMedia}   type
     *
     * @return {string}
     */
    getNodeMedia (node: ContentNode, type: NodeMedia): string {
        const parts = type.split(".");

        const domain = parts[0] as string;
        const media = parts[1] as string;

        for (const [key, value] of Object.entries(node)) {
            if (key === domain) {
                return value[media];
            }
        }

        return "";
    },

    /**
     * Lookup the path the filesystem path to a media type.
     *
     * @param  {NodeMedia} type
     *
     * @return {string}
     */
    getNodeMediaPath (type: NodeMedia): string {
        return nodeMediaPathMap.get(type) ?? "";
    },

    /**
     * Check if a node has a specific type of media.
     *
     * @param  {ContentNode} node
     * @param  {NodeMedia}   type
     *
     * @return {boolean}
     */
    nodeHasMedia (node: ContentNode, type: NodeMedia): boolean {
        return this.getNodeMedia(node, type) !== "";
    },

    /**
     * Format node media URL. If media type does not exist on node instance the
     * filename portion will be an empty string.
     *
     * @see Utils.nodeHasMedia()
     *
     * @param  {ContentNode} node
     * @param  {NodeMedia}   type
     *
     * @return {string}
     */
    nodeMediaUrl (node: ContentNode, type: NodeMedia): string {
        const media = this.getNodeMedia(node, type);

        // Handle absolute paths
        if (media.startsWith("/")) {
            return media;
        }

        const path = this.getNodeMediaPath(type);

        // Handle relative paths
        return path + "/" + media;
    },

    /**
     * Get the a content nodes page media meta data.
     *
     * @param  {ContentNode}      node
     *
     * @return {ContentImageMeta}
     */
    nodeMediaMeta (node: ContentNode): ContentImageMeta {
        return node.page?.imageMeta ?? {} as ContentImageMeta;
    }
};

export default Utils;
